import { orgSelectors } from 'ducks/orgs'
import { HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2 } from 'elements/hardwareFilter/constantsV2'
import { DISTRIBUTOR_FILTER_KEY_V2 } from 'elements/hardwareFilter/distributor/constants'
import HardwareSelectorFilterSidebar from 'elements/hardwareSelectorV2/elements/HardwareSelectorFilterSidebar'
import HardwareSelectorLayout from 'elements/hardwareSelectorV2/elements/HardwareSelectorLayout'
import HardwareSelectorList from 'elements/hardwareSelectorV2/elements/HardwareSelectorList'
import HardwareSelectorToolBars, {
  getPlaceholderByComponentType,
} from 'elements/hardwareSelectorV2/elements/HardwareSelectorToolBars'
import useHardwareSelectorFetchEngine from 'elements/hardwareSelectorV2/fetch/useHardwareSelectorFetchEngine'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import HardwareSelectorDialogV2 from 'elements/hardwareSelectorV2/HardwareSelectorDialogV2'
import Pagination from 'elements/pagination/Pagination'
import { useDynamicFilterValues } from 'hooks/useDynamicFilterValues'
import { capitalize } from 'lodash'
import { ComponentTypesV2 } from 'opensolar-common'
import { CloseIcon, IconButton, Typography, useTheme } from 'opensolar-ui'
import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import { useTranslate } from 'ra-core'
import { useCallback, useMemo } from 'react'
import { Form } from 'react-final-form'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import { BomComponentSelectorType } from './AddItemDialog'
import { StyledDialogTitle } from './components'
import { getComponentSelectorBrowseTypes } from './getComponentSelectorBrowseTypes'

interface ComponentSelectorDialogProps {
  handleClose: () => void
  handleSelect: (value: { [key: string]: any }) => void
  componentType: BomComponentSelectorType
  /** Skip project permission check - use when not in a project context (e.g., Order By Project Cart) */
  skipProjectPermissionCheck?: boolean
}

const ComponentSelectorDialog: React.FC<ComponentSelectorDialogProps> = ({
  handleClose,
  handleSelect,
  componentType,
  skipProjectPermissionCheck = false,
}) => {
  const theme = useTheme()
  const translate = useTranslate()
  const dynamicFilterValues = useDynamicFilterValues()

  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()

  // Check if the distributor has trade pricing available. We use this to suppress
  // public pricing for some distributors.
  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId: useSelector(orgSelectors.getOrg)?.id || 0,
    distributors: hardwareDistributors,
  })

  const defaultFilterValues = useMemo(() => {
    const values = { ...dynamicFilterValues }
    delete values[DISTRIBUTOR_FILTER_KEY_V2]
    return values
  }, [dynamicFilterValues])

  const componentTypes = useMemo(() => getComponentSelectorBrowseTypes(componentType), [componentType])

  const dialogTitle = useMemo(() => {
    if (componentType === 'other') {
      return 'Select Other Component'
    } else if (componentType === 'mounting') {
      return 'Select Mounting Component'
    } else if (componentType === 'module') {
      return 'Select Panel'
    }
    return `Select ${capitalize(`${componentType}`)}`
  }, [componentType])

  const {
    componentsData,
    setFilters,
    total = 0,
    loading,
    page,
    perPage,
    setPage,
    setPerPage,
    persistentFilterValues,
    modifiableFilterValues,
    allFilterValues,
  } = useHardwareSelectorFetchEngine({
    componentTypes: componentTypes,
    limit: 20,
    defaultFilterValues,
  })

  const handleUpdate = useCallback(
    (newFilterValues: { [key: string]: unknown }) => {
      setFilters({ ...modifiableFilterValues, ...newFilterValues })
    },
    [modifiableFilterValues]
  )

  const handleSearchTextUpdate = useCallback(
    (searchText) => {
      handleUpdate({ search: searchText })
    },
    [handleUpdate]
  )

  const doHandleSelect = useCallback(
    (component, costSource, price) => {
      handleSelect({ ...component, priceSource: costSource, price })
    },
    [handleSelect]
  )

  const hiddenColumns = new Set<string>().add('price').add('stock')
  const shouldShowRating = componentTypes.some((type) => ['module', 'inverters', 'battery'].includes(type))
  if (!shouldShowRating) {
    hiddenColumns.add('rating')
  }

  return (
    <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
      <Form onSubmit={() => { }} initialValues={{}}>
        {() => (
          <HardwareSelectorDialogV2 isOpen={true} onClose={handleClose}>
            <StyledDialogTitle>
              <Typography textVariant="modalTitle" colorHex={theme.palette.neutralGrey[1300] as `#${string}`}>
                {dialogTitle}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon color={theme.palette.neutralGrey[800]} size={16} />
              </IconButton>
            </StyledDialogTitle>
            <HardwareSelectorLayout
              listContent={
                <HardwareSelectorList
                  total={total}
                  loading={loading}
                  data={componentsData}
                  onSelect={doHandleSelect}
                  Pagination={
                    <Pagination total={total} page={page} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
                  }
                  hiddenColumnGroups={hiddenColumns}
                  skipProjectPermissionCheck={skipProjectPermissionCheck}
                />
              }
              toolBars={
                <HardwareSelectorToolBars
                  placeholder={getPlaceholderByComponentType(componentType as ComponentTypesV2, translate)}
                  onChange={handleSearchTextUpdate}
                  componentType={componentType as ComponentTypesV2}
                />
              }
              sidebar={
                <HardwareSelectorFilterSidebar
                  hardwareFilterConfigList={HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2}
                  persistentFilterValues={persistentFilterValues}
                  modifiableFilterValues={modifiableFilterValues}
                  allFilterValues={allFilterValues}
                  setModifiableFilterValues={setFilters}
                  componentTypes={componentTypes}
                  location="design"
                />
              }
            />
          </HardwareSelectorDialogV2>
        )}
      </Form>
    </DistributorTradePricingContext.Provider>
  )
}

export default ComponentSelectorDialog
